import { usePreferredSideFit } from "@/components/hooks/usePreferredSideFit";
import { useStablePlacement } from "@/components/hooks/useStablePlacement";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import "./NestedDropdownMenuItem.scss";

export interface NestedDropdownMenuItemProps {
  label: string;
  iconName?: string;
  onClick?: () => void;
  hoverComponent: React.ReactNode; // component to render on hover
}

/**
 * NestedDropdownMenuItem
 *
 * Renders a dropdown menu item that displays a floating submenu panel on hover.
 *
 * Behavior:
 * - The submenu panel prefers to open to the right of the trigger element.
 * - If insufficient horizontal space exists on the right, it flips to open on the left.
 * - If neither side fits horizontally, it falls back to vertical placement (down or up).
 *
 * Implementation details:
 * - Uses `usePreferredSideFit` hooks to measure available space and determine fit horizontally and vertically.
 * - Uses `useStablePlacement` to apply hysteresis and stabilize placement decisions.
 * - Final placement is expressed via CSS class names; no inline styles are applied.
 *
 * Props:
 * - `label` (string): Text displayed in the trigger row.
 * - `iconName` (string, optional): Material icon name to display alongside the label.
 * - `onClick` (() => void, optional): Click handler for the trigger row.
 * - `hoverComponent` (React.ReactNode): Content rendered inside the floating submenu panel when open.
 */

export const NestedDropdownMenuItem: React.FC<NestedDropdownMenuItemProps> = ({
  iconName,
  label,
  onClick,
  hoverComponent,
}) => {
  const [open, setOpen] = useState(false);
  const itemRef = useRef<HTMLLIElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const openTimer = useRef<number | null>(null);

  // Measure horizontal/vertical fit & space
  const {
    fitsRight,
    fitsDown,
    recalc: _recalc,
    spaces,
    hNeeded,
    vNeeded,
  } = usePreferredSideFit(itemRef, panelRef, { open });

  const hSpaces = spaces ? { left: spaces.left, right: spaces.right } : null;
  const vSpaces = spaces ? { up: spaces.up, down: spaces.down } : null;

  const placement = useStablePlacement({
    open,
    fitsRight,
    hSpaces, // may be null until measured
    hNeeded,
    fitsDown,
    vSpaces, // may be null until measured
  });

  const placementReady = open && !!hSpaces && !!vSpaces;
  const panelVisibilityStyle = placementReady ? undefined : ({ opacity: 0, pointerEvents: "none" } as const);

  useEffect(() => {
    return () => {
      if (closeTimer.current !== null) {
        clearTimeout(closeTimer.current);
        closeTimer.current = null;
      }
      if (openTimer.current !== null) {
        clearTimeout(openTimer.current);
        openTimer.current = null;
      }
    };
  }, []);

  return (
    <li
      className={"dropdown-menu-item nested-dropdown-menu-item"}
      ref={itemRef}
      onMouseEnter={() => {
        if (closeTimer.current !== null) {
          clearTimeout(closeTimer.current);
          closeTimer.current = null;
        }
        if (openTimer.current !== null) {
          clearTimeout(openTimer.current);
          openTimer.current = null;
        }
        openTimer.current = window.setTimeout(() => {
          setOpen(true);
          openTimer.current = null;
        }, 300);
      }}
      onMouseLeave={() => {
        if (openTimer.current !== null) {
          clearTimeout(openTimer.current);
          openTimer.current = null;
        }
        if (closeTimer.current !== null) {
          clearTimeout(closeTimer.current);
          closeTimer.current = null;
        }
        closeTimer.current = window.setTimeout(() => {
          setOpen(false);
          closeTimer.current = null;
        }, 150);
      }}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: <explanation> */}
      <span
        className="dropdown-menu-item__button"
        onClick={(e) => {
          e.stopPropagation();
          onClick?.();
        }}
      >
        {iconName && <span className="dropdown-menu-item__icon material-symbols-outlined">{iconName}</span>}
        <span className="dropdown-menu-item__label">{label}</span>
        <span className="dropdown-menu-item__chevron material-symbols-outlined">chevron_right</span>
      </span>

      {open && (
        // Classes express final placement; CSS handles positioning/sizing
        <div
          className={`nested-dropdown-menu-item__panel ${
            placement === "right" ? "nested-dropdown-menu-item__panel--right" : ""
          } ${placement === "left" ? "nested-dropdown-menu-item__panel--left" : ""} ${
            placement === "down" ? "nested-dropdown-menu-item__panel--down" : ""
          } ${placement === "up" ? "nested-dropdown-menu-item__panel--up" : ""}`}
          style={panelVisibilityStyle}
          ref={panelRef}
          role="dialog"
          aria-label={`${label} submenu`}
        >
          {hoverComponent}
        </div>
      )}
    </li>
  );
};
